import ExcelJS from "exceljs";
import { formatDateTime } from "../helpers";
import formatCell from "./formatCell";
import { resolveLogoDataUrl } from "./loadImage";
import downloadBlob from "./downloadBlob";
import defaultLogo from "@/assets/logo-print-3.png";
import { HEX } from "./colors";

const LOGO_ROWS = 3;

function slugify(title) {
  return (title || "report")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function imageExtension(dataUrl) {
  const match = /^data:image\/(png|jpeg|jpg|gif)/.exec(dataUrl || "");
  const ext = match ? match[1] : "png";
  return ext === "jpg" ? "jpeg" : ext;
}

function fill(color) {
  return { type: "pattern", pattern: "solid", fgColor: { argb: color } };
}

function setEyebrowCell(cell, label, value, align) {
  cell.value = {
    richText: [
      {
        text: `${label.toUpperCase()}\n`,
        font: { size: 8, color: { argb: HEX.MUTED_SOFT }, bold: false }
      },
      {
        text: value,
        font: { size: 10, bold: true, color: { argb: HEX.INK_900 } }
      }
    ]
  };
  cell.alignment = { horizontal: align, wrapText: true, vertical: "top" };
}

export default async function exportReportExcel(input) {
  const cols = (input.cols || []).filter(c => c.column !== "action");
  const rows = input.rows || [];
  const colCount = Math.max(cols.length, 3);

  const businessLogo = await resolveLogoDataUrl(
    input.businessLogoUrl,
    defaultLogo
  );

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Report", {
    views: [{ showGridLines: false }]
  });

  sheet.columns = cols.map(c => ({
    width: c.width ? Math.max(Number(c.width) / 6, 10) : 18
  }));

  let r = 1;

  for (let i = 0; i < LOGO_ROWS; i++) {
    sheet.getRow(r + i).height = 18;
  }
  if (businessLogo) {
    const imageId = workbook.addImage({
      base64: businessLogo,
      extension: imageExtension(businessLogo)
    });
    sheet.addImage(imageId, {
      tl: { col: 0, row: r - 1 },
      ext: { width: 130, height: 40 }
    });
  }
  r += LOGO_ROWS;

  sheet.mergeCells(r, 1, r, colCount);
  const titleCell = sheet.getCell(r, 1);
  titleCell.value = input.title || "Report";
  titleCell.font = { size: 16, bold: true, color: { argb: HEX.INK_900 } };
  titleCell.alignment = { horizontal: "center" };
  sheet.getRow(r).height = 24;
  r += 1;

  if (input.subtitle) {
    sheet.mergeCells(r, 1, r, colCount);
    const subtitleCell = sheet.getCell(r, 1);
    subtitleCell.value = input.subtitle;
    subtitleCell.font = { size: 11, color: { argb: HEX.MUTED } };
    subtitleCell.alignment = { horizontal: "center" };
    r += 1;
  }
  r += 1;

  const hasDateRange =
    input.dateRange && (input.dateRange.start || input.dateRange.end);
  if (hasDateRange) {
    sheet.mergeCells(r, 1, r, Math.ceil(colCount / 2));
    setEyebrowCell(
      sheet.getCell(r, 1),
      "Report Period",
      `${input.dateRange.start || ""} — ${input.dateRange.end || ""}`,
      "left"
    );
  }
  sheet.mergeCells(r, Math.ceil(colCount / 2) + 1, r, colCount);
  setEyebrowCell(
    sheet.getCell(r, Math.ceil(colCount / 2) + 1),
    "Printed On",
    formatDateTime(new Date()),
    "right"
  );
  sheet.getRow(r).height = 26;
  r += 1;

  if (input.filters && input.filters.length) {
    const filterLine = input.filters
      .filter(f => f.value)
      .map(f => `${f.label}: ${f.value}`)
      .join("   |   ");
    if (filterLine) {
      sheet.mergeCells(r, 1, r, colCount);
      const filtersCell = sheet.getCell(r, 1);
      filtersCell.value = filterLine;
      filtersCell.font = {
        size: 9,
        italic: true,
        color: { argb: HEX.MUTED_SOFT }
      };
      r += 1;
    }
  }
  r += 1;

  const headerRowIndex = r;
  const headerRow = sheet.getRow(headerRowIndex);
  cols.forEach((c, idx) => {
    const cell = headerRow.getCell(idx + 1);
    cell.value = c.label;
    cell.font = { bold: true, color: { argb: HEX.WHITE } };
    cell.fill = fill(HEX.ACCENT_600);
    cell.alignment = {
      horizontal: c.formatAmount || c.sum ? "right" : "left",
      vertical: "middle"
    };
  });
  headerRow.height = 20;
  r += 1;

  rows.forEach((row, rIdx) => {
    const dataRow = sheet.getRow(r);
    cols.forEach((c, idx) => {
      const cell = dataRow.getCell(idx + 1);
      cell.value = formatCell(row, c, rIdx);
      cell.alignment = {
        horizontal: c.formatAmount || c.sum ? "right" : "left"
      };
      if (rIdx % 2 === 1) {
        cell.fill = fill(HEX.SURFACE_2);
      }
      cell.border = { bottom: { style: "hair", color: { argb: HEX.LINE } } };
    });
    r += 1;
  });

  r += 1;

  if (input.summary && input.summary.length) {
    input.summary.forEach((item, idx) => {
      const isLast = idx === input.summary.length - 1;
      const labelCell = sheet.getCell(r, colCount - 1);
      const valueCell = sheet.getCell(r, colCount);
      labelCell.value = item.label;
      labelCell.font = isLast
        ? { bold: true, color: { argb: HEX.INK_900 } }
        : { color: { argb: HEX.MUTED } };
      labelCell.alignment = { horizontal: "right" };
      valueCell.value = item.value;
      valueCell.font = { bold: true, color: { argb: HEX.INK_900 } };
      valueCell.alignment = { horizontal: "right" };
      if (isLast) {
        labelCell.fill = fill(HEX.ACCENT_050);
        valueCell.fill = fill(HEX.ACCENT_050);
        labelCell.border = {
          left: { style: "medium", color: { argb: HEX.ACCENT_600 } }
        };
      }
      r += 1;
    });
    r += 1;
  }

  const signOff = [
    { label: "Printed By", value: input.printedBy || "-" },
    { label: "Checked By", value: "-" },
    { label: "Approved By", value: "-" }
  ];
  const signOffSpan = Math.max(Math.floor(colCount / 3), 1);
  signOff.forEach((item, idx) => {
    const startCol = idx * signOffSpan + 1;
    const endCol =
      idx === signOff.length - 1 ? colCount : startCol + signOffSpan - 1;
    sheet.mergeCells(r, startCol, r, endCol);
    const valueCell = sheet.getCell(r, startCol);
    valueCell.value = item.value;
    valueCell.font = { bold: true, color: { argb: HEX.INK_900 } };
    valueCell.border = { top: { style: "hair", color: { argb: HEX.LINE } } };

    sheet.mergeCells(r + 1, startCol, r + 1, endCol);
    const labelCell = sheet.getCell(r + 1, startCol);
    labelCell.value = item.label.toUpperCase();
    labelCell.font = { size: 8, bold: true, color: { argb: HEX.MUTED } };
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  });
  downloadBlob(blob, `${input.fileName || slugify(input.title)}.xlsx`);
}
